'use client'
import React, { useState } from 'react'
import { useRouter } from 'next/navigation'
import { CircleCheck } from 'lucide-react'
import { useTranslations } from 'use-intl'
import Button from 'components/commercetools-ui/atoms/button'
import Heading from 'components/commercetools-ui/atoms/heading'
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image'
import Input from 'components/commercetools-ui/atoms/input'
import ZooIcon from 'components/icons/zooIcon'
import { tryConfigLoader } from 'config/helper'
import { TOP_PROGRESS_START } from 'helpers/constants/events'

export interface NewsletterProps {
  headline?: string;
  headline2?: string;
  description?: string;
  advantages?: Advantage[];
  inputPlaceholder?: string;
  ctaLabel?: string;
  disclaimer?: string;
  image?: ImageProps;
  bubbleText1?: string;
  bubbleText2?: string;
}

export type Advantage = {
  advantage: string;
};

export default function Newsletter({
  headline,
  headline2,
  description,
  advantages,
  inputPlaceholder,
  ctaLabel,
  disclaimer,
  image,
  bubbleText1,
  bubbleText2,
}: NewsletterProps) {
  //i18n messages
  const translate = useTranslations()
  const router = useRouter()
  const newsletterSlug = tryConfigLoader('contentstackNewsletterSlug.slug')
  //messages
  const fallbackHeadline = translate('newsletter.headline-fallback')
  const fallbackDescription = translate('newsletter.description-fallback')
  const fallbackPlaceholder = translate('newsletter.placeholder-fallback')
  const fallbackCta = translate('newsletter.cta-fallback')
  const fallbackDisclaimer = translate('newsletter.disclaimer-fallback')

  //form data
  const [data, setData] = useState({ email: '' })

  //input change handler
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value })
  }

  //submission handler — hands the entered email off to the /newsletter page,
  //which pre-fills it and runs the actual registration + validation
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    document.dispatchEvent(new Event(TOP_PROGRESS_START))
    router.push(`/${newsletterSlug}?email=${encodeURIComponent(data.email)}`)
  }

  return (
    <div className="mx-auto w-full bg-primary">
      <div className="relative mx-auto flex max-w-7xl flex-col px-16 pb-40 text-white md:px-70 md:py-80">
        <div className='flex h-123 justify-between md:hidden'>
          <div className='relative mt-16 size-80 pt-16 text-center'>
            <p className='font-heading relative z-10 text-24 leading-6'>{bubbleText1}</p>
            <p className='font-heading relative z-20 text-base'>{bubbleText2}</p>
            <ZooIcon name='footer-bubble' size={80} className='absolute left-0 top-0 z-0 text-secondary ' autoStrokeColor />
          </div>
          <Image {...image} width={145} height={123} className='w-145 object-contain' />
        </div>
        <div className="flex w-full max-w-350 flex-col">
          <div className='relative'>
            <Heading level={2}>
              {headline || fallbackHeadline}
            </Heading>
            <Heading level={2}>
              {headline2}
            </Heading>
            <p className="font-heading mt-3 text-base font-bold">{description || fallbackDescription}</p>
            <ul className='mb-24 mt-16 flex flex-col gap-12'>
              {advantages?.map((advantage, index) => (
                <li key={index} className='flex items-center gap-11'>
                  <CircleCheck size={28} fill="#fff" className='text-primary' />
                  <span>{advantage.advantage}</span>
                </li>
              ))}
            </ul>
            <div className='absolute -right-280 top-51 hidden size-215 text-center md:block'>
              <div className='relative flex h-215 flex-col items-center justify-center'>
                <ZooIcon name='footer-bubble-big' size={215} className='absolute left-0 top-0 text-secondary' autoStrokeColor />
                <p className='font-heading relative z-10 text-40 leading-3xl'>{bubbleText1}</p>
                <p className='font-heading relative z-10 text-40 leading-3xl'>{bubbleText2}</p>
              </div>
            </div>
          </div>

          <form action="#" className="mt-4 flex w-full flex-col gap-16" onSubmit={onSubmit}>
            <label htmlFor="cta-email" className="sr-only">
              <>{inputPlaceholder || fallbackPlaceholder}</>
            </label>
            <Input
              id="cta-email"
              name="email"
              type="email"
              className="block w-full placeholder:text-neutral-600"
              placeholder={inputPlaceholder}
              onChange={onChange}
              errorClassName='focus:!border-2'
              hasPetrolBgColor
            />
            <Button
              variant="secondary"
              size="xs"
              type='submit'
              className='!outline-2 focus:!border-none focus-visible:!outline-secondary'
            >
              <>{ctaLabel || fallbackCta}</>
            </Button>
          </form>
        </div>
        <div className="pt-32">
          <div className="text-left text-xs text-white [&_a]:underline" dangerouslySetInnerHTML={{ __html: disclaimer || fallbackDisclaimer }} />
        </div>
        <Image {...image} className='absolute right-0 top-0 hidden w-145 md:block lg:w-240 xl:w-[460px]' />
      </div>
    </div>
  )
}
